import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type ReactionEmoji = '👍' | '👎' | '❤️' | '👏' | '😂' | '😮';

export interface Reaction {
  id: string;
  participantId: string;
  participantName: string;
  emoji: ReactionEmoji;
  timestamp: string;
}

interface UseReactionsOptions {
  currentUserId: string;
  currentUserName: string;
  audioVideo: any; // AudioVideoFacade from Chime SDK
}

interface UseReactionsReturn {
  reactions: Reaction[];
  sendReaction: (emoji: ReactionEmoji) => void;
  clearReaction: (id: string) => void;
}

const REACTION_TOPIC = 'REACTION';
const REACTION_DURATION = 3000; // 3 seconds

export function useReactions({
  currentUserId,
  currentUserName,
  audioVideo,
}: UseReactionsOptions): UseReactionsReturn {
  const [reactions, setReactions] = useState<Reaction[]>([]);

  // Subscribe to incoming reactions
  useEffect(() => {
    if (!audioVideo) return;

    const handleDataMessage = (dataMessage: any) => {
      try {
        const data = JSON.parse(dataMessage.text());
        
        if (data.type === 'REACTION') {
          const reaction: Reaction = {
            id: data.id,
            participantId: data.participantId,
            participantName: data.participantName,
            emoji: data.emoji,
            timestamp: data.timestamp,
          };

          setReactions((prev) => [...prev, reaction]);

          // Auto-remove reaction after duration
          setTimeout(() => {
            setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
          }, REACTION_DURATION);
        }
      } catch (error) {
        console.error('Error parsing reaction message:', error);
      }
    };

    try {
      audioVideo.realtimeSubscribeToReceiveDataMessage(REACTION_TOPIC, handleDataMessage);
    } catch (error) {
      console.error('Error subscribing to reactions:', error);
    }

    return () => {
      try {
        audioVideo.realtimeUnsubscribeFromReceiveDataMessage(REACTION_TOPIC);
      } catch (error) {
        console.error('Error unsubscribing from reactions:', error);
      }
    };
  }, [audioVideo]);

  const sendReaction = useCallback(
    (emoji: ReactionEmoji) => {
      if (!audioVideo) return;

      const reaction: Reaction = {
        id: uuidv4(),
        participantId: currentUserId,
        participantName: currentUserName,
        emoji,
        timestamp: new Date().toISOString(),
      };

      // Add to local reactions immediately
      setReactions((prev) => [...prev, reaction]);

      // Auto-remove after duration
      setTimeout(() => {
        setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
      }, REACTION_DURATION);

      // Send via DataMessage
      try {
        const dataMessage = JSON.stringify({
          type: 'REACTION',
          id: reaction.id,
          participantId: reaction.participantId,
          participantName: reaction.participantName,
          emoji: reaction.emoji,
          timestamp: reaction.timestamp,
        });

        audioVideo.realtimeSendDataMessage(REACTION_TOPIC, dataMessage);
      } catch (error) {
        console.error('Error sending reaction:', error);
      }
    },
    [audioVideo, currentUserId, currentUserName]
  );

  const clearReaction = useCallback((id: string) => {
    setReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  return {
    reactions,
    sendReaction,
    clearReaction,
  };
}
